import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { StoreProvider } from "@/lib/theme";
import { detectTenantFromHost } from "@/lib/tenant-resolver";

import Landing from "@/pages/Landing";

import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminTenants from "@/pages/admin/AdminTenants";
import AdminPlans from "@/pages/admin/AdminPlans";
import AdminPayments from "@/pages/admin/AdminPayments";

import TenantLogin from "@/pages/tenant/TenantLogin";
import TenantLayout from "@/pages/tenant/TenantLayout";
import TenantDashboard from "@/pages/tenant/TenantDashboard";
import TenantProducts from "@/pages/tenant/TenantProducts";
import TenantTaxonomy from "@/pages/tenant/TenantTaxonomy";
import TenantBanners from "@/pages/tenant/TenantBanners";
import TenantOffers from "@/pages/tenant/TenantOffers";
import TenantOrders from "@/pages/tenant/TenantOrders";
import TenantBranding from "@/pages/tenant/TenantBranding";
import TenantSellers from "@/pages/tenant/TenantSellers";
import TenantOps from "@/pages/tenant/TenantOps";

import StorefrontLayout from "@/pages/store/StorefrontLayout";
import StorefrontHome from "@/pages/store/StorefrontHome";
import StorefrontProduct from "@/pages/store/StorefrontProduct";
import StorefrontCheckout from "@/pages/store/StorefrontCheckout";
import StorefrontLogin from "@/pages/store/StorefrontLogin";
import CustomerDashboard from "@/pages/store/CustomerDashboard";

import SellerLogin from "@/pages/seller/SellerLogin";
import SellerDashboard from "@/pages/seller/SellerDashboard";

function RequireRole({ roles, redirect, children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center text-slate-500">Loading…</div>;
  if (!user || !roles.includes(user.role)) return <Navigate to={redirect} replace />;
  return children;
}

export default function App() {
  // Subdomain tenant auto-rewrite: aquapure.rentalapp.com → /t/aquapure (in URL)
  const subdomainSlug = detectTenantFromHost();
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster richColors position="top-right" />
        <Routes>
          <Route path="/" element={subdomainSlug ? <Navigate to={`/t/${subdomainSlug}`} replace /> : <Landing />} />

          {/* Super Admin */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route
            path="/admin"
            element={
              <RequireRole roles={["super_admin"]} redirect="/admin/login">
                <AdminLayout />
              </RequireRole>
            }
          >
            <Route index element={<AdminDashboard />} />
            <Route path="tenants" element={<AdminTenants />} />
            <Route path="plans" element={<AdminPlans />} />
            <Route path="payments" element={<AdminPayments />} />
          </Route>

          {/* Tenant Admin (owner / staff / sales / tech) */}
          <Route path="/tenant/:slug/admin/login" element={<TenantLogin />} />
          <Route
            path="/tenant/:slug/admin"
            element={
              <RequireRole roles={["tenant_owner", "tenant_staff", "sales_staff", "service_tech"]} redirect="login">
                <TenantLayout />
              </RequireRole>
            }
          >
            <Route index element={<TenantDashboard />} />
            <Route path="products" element={<TenantProducts />} />
            <Route path="taxonomy" element={<TenantTaxonomy />} />
            <Route path="banners" element={<TenantBanners />} />
            <Route path="offers" element={<TenantOffers />} />
            <Route path="orders" element={<TenantOrders />} />
            <Route path="ops" element={<TenantOps />} />
            <Route path="sellers" element={<TenantSellers />} />
            <Route path="branding" element={<TenantBranding />} />
          </Route>

          {/* Seller */}
          <Route path="/tenant/:slug/seller/login" element={<SellerLogin />} />
          <Route
            path="/tenant/:slug/seller"
            element={
              <RequireRole roles={["seller"]} redirect="login">
                <SellerDashboard />
              </RequireRole>
            }
          />

          {/* Storefront + Customer */}
          <Route path="/t/:slug" element={<StoreProvider><StorefrontLayout /></StoreProvider>}>
            <Route index element={<StorefrontHome />} />
            <Route path="product/:pid" element={<StorefrontProduct />} />
            <Route path="checkout" element={<StorefrontCheckout />} />
            <Route path="login" element={<StorefrontLogin />} />
            <Route path="account" element={<CustomerDashboard />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
